import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { CONVERTED } from "../lib/gtag";
import DesignerPreview, { SIZE_DIMS } from "./DesignerPreview";
import ResurfacingSystem from "./ResurfacingSystem";
import { COURT_COLOURS } from "../data/content";

const STEPS = ["Surface", "Size", "Colours", "Game Lines", "Logo", "Extras", "Submit"];
const FINISHES = ["Acrylic Hard Court", "Cushioned Acrylic", "Premium Tile System"];
const SIZES = [
  { id: "full", label: "Full Court", dims: "28m × 15m" },
  { id: "half", label: "Half Court", dims: "14m × 15m" },
  { id: "threes", label: "3x3 Court", dims: "15m × 11m" },
  { id: "junior", label: "Junior Court", dims: "10m × 7m" },
  { id: "custom", label: "Custom / Fit My Slab", dims: "your slab size" },
];
const SPORTS = ["Basketball", "Netball", "Tennis", "Pickleball"];
const EXTRAS = ["Hoop system", "Centre logo", "LED lighting", "Fencing", "Netball posts", "Tennis net", "Rebounder wall"];
const ZONES = [
  ["border", "Border / Apron"],
  ["base", "Main Surface"],
  ["key", "Key / Lane"],
  ["centre", "Centre Circle"],
  ["lines", "Primary Lines"],
];

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-sm border px-4 py-2.5 text-sm transition-colors ${active ? "border-neon text-neon bg-neon/5" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
    {children}
  </button>
);

export default function CourtDesigner() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [surfaceType, setSurfaceType] = useState("Outdoor");
  const [finish, setFinish] = useState(FINISHES[0]);
  const [size, setSize] = useState("full");
  const [customDims, setCustomDims] = useState({ length: 12, width: 8 });
  const [zones, setZones] = useState({ border: "#141414", base: "#2E6FB0", key: "#B5502E", centre: "#B5502E", lines: "#F2F2F2" });
  const [activeZone, setActiveZone] = useState("base");
  const [sports, setSports] = useState(["Basketball"]);
  const [extras, setExtras] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", postcode: "" });
  const [sending, setSending] = useState(false);
  const [logoText, setLogoText] = useState("SPEEDY RESURFACING");
  const [logoColor, setLogoColor] = useState("#FFD84A");

  // Restore a shared design from a ?d= link
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("d");
    if (!raw) return;
    try {
      const d = JSON.parse(atob(decodeURIComponent(raw)));
      if (d.zones) setZones(d.zones);
      if (d.sports?.length) setSports(d.sports);
      if (d.size) setSize(d.size);
      if (d.customDims) setCustomDims(d.customDims);
      if (d.surfaceType) setSurfaceType(d.surfaceType);
      if (d.finish) setFinish(d.finish);
      if (Array.isArray(d.extras)) setExtras(d.extras);
      if (d.logoText !== undefined) setLogoText(d.logoText);
      if (d.logoColor) setLogoColor(d.logoColor);
      toast.success("Design loaded from link");
    } catch {
      /* malformed link — start fresh */
    }
  }, []);

  const shareLink = () => {
    const d = { surfaceType, finish, size, customDims, zones, sports, extras, logoText, logoColor };
    return `${window.location.origin}/customiser?d=${encodeURIComponent(btoa(JSON.stringify(d)))}`;
  };
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink());
    toast.success("Design link copied — share it anywhere");
  };

  const toggleSport = (s) =>
    setSports((cur) => cur.includes(s) ? (cur.length > 1 ? cur.filter((x) => x !== s) : cur) : (cur.length < 3 ? [...cur, s] : cur));
  const toggleExtra = (e) => setExtras((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));

  const colourName = (hex) => COURT_COLOURS.find((c) => c.hex === hex)?.name || hex;
  const sizeInfo = SIZES.find((s) => s.id === size);
  const dims = size === "custom"
    ? { length: Math.min(Math.max(Number(customDims.length) || 12, 4), 60), width: Math.min(Math.max(Number(customDims.width) || 8, 3), 40) }
    : SIZE_DIMS[size];
  const area = Math.round(dims.length * dims.width);
  const sizeLabel = size === "custom" ? `Custom · ${dims.length}m × ${dims.width}m` : `${sizeInfo.label} (${sizeInfo.dims})`;

  const submit = async () => {
    if (!contact.name || (!contact.email && !contact.phone)) {
      toast.error("Add your name and an email or phone so we can quote you");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", {
        type: "customiser",
        contact_name: contact.name, email: contact.email || null, phone: contact.phone || null,
        postcode: contact.postcode || null, court_type: sports.join(" + "),
        data: {
          surface_type: surfaceType, finish, size: sizeLabel, area: `${area} m²`,
          border_colour: colourName(zones.border), main_surface: colourName(zones.base),
          key_colour: colourName(zones.key), centre_colour: colourName(zones.centre),
          line_colour: colourName(zones.lines), game_lines: sports.join(", "),
          extras: extras.join(", ") || "None",
          centre_logo: logoText ? logoText.toUpperCase() : "None",
          design_link: shareLink(),
        },
      });
      nav("/thank-you", CONVERTED("customiser"));
    } catch {
      toast.error("Something went wrong — please try again");
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start" data-testid="court-designer">
      {/* Preview */}
      <div className="min-w-0 overflow-hidden lg:sticky lg:top-28 rounded-lg border border-white/10 bg-obsidian-800 p-5 md:p-10 grid-lines">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neon">Live Preview · {sizeInfo.label} · ≈{area} m²</span>
          <button onClick={copyShareLink} data-testid="designer-share-link"
            className="inline-flex items-center gap-1.5 py-2 font-mono text-[10px] uppercase tracking-widest text-gold hover:text-gold-hover transition-colors">
            <Link2 size={12} /> Copy Design Link
          </button>
        </div>
        <DesignerPreview zones={zones} sports={sports} size={size} dims={dims}
          tiles={finish === "Premium Tile System"} logoText={logoText} logoColor={logoColor} />
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          {sports.map((s, i) => <span key={s}>{i === 0 ? "●" : "○"} {s}</span>)}
        </div>
      </div>

      {/* Steps */}
      <div className="min-w-0">
        <div className="flex flex-wrap gap-1.5 mb-8">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} data-testid={`designer-step-${i}`}
              className={`rounded-sm px-3 py-2.5 md:py-1.5 font-mono text-[10px] uppercase tracking-wider border transition-colors ${
                i === step ? "border-neon text-neon" : i < step ? "border-gold/40 text-gold" : "border-white/10 text-zinc-500"}`}>
              {String(i + 1).padStart(2, "0")} {s}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
            {step === 0 && (
              <div className="space-y-6">
                <h3 className="font-heading font-black text-2xl">SURFACE.</h3>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Location</p>
                  <div className="flex gap-2">
                    {["Outdoor", "Indoor"].map((t) => <Chip key={t} active={surfaceType === t} onClick={() => setSurfaceType(t)} testid={`surface-${t.toLowerCase()}`}>{t}</Chip>)}
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Finish</p>
                  <div className="flex flex-wrap gap-2">
                    {FINISHES.map((f) => <Chip key={f} active={finish === f} onClick={() => setFinish(f)} testid={`finish-${f.split(" ")[0].toLowerCase()}`}>{f}</Chip>)}
                  </div>
                </div>
                <ResurfacingSystem finish={finish} />
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-heading font-black text-2xl">SIZE.</h3>
                {SIZES.map((s) => (
                  <button key={s.id} onClick={() => setSize(s.id)} data-testid={`size-${s.id}`}
                    className={`w-full flex items-center justify-between rounded-sm border px-5 py-4 transition-colors ${size === s.id ? "border-neon bg-neon/5" : "border-white/10 hover:border-white/30"}`}>
                    <span className={`font-bold ${size === s.id ? "text-neon" : "text-white"}`}>{s.label}</span>
                    <span className="font-mono text-xs text-zinc-500">{s.dims}</span>
                  </button>
                ))}
                {size === "custom" && (
                  <div className="rounded-sm border border-gold/25 bg-obsidian-800 p-4" data-testid="custom-dims">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gold mb-3">Your slab dimensions (metres)</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[["length", "Length (m)"], ["width", "Width (m)"]].map(([k, label]) => (
                        <div key={k}>
                          <label className="text-xs text-zinc-500">{label}</label>
                          <input type="number" min={k === "length" ? 4 : 3} max={k === "length" ? 60 : 40} step="0.5"
                            value={customDims[k]} data-testid={`custom-${k}`}
                            onChange={(e) => setCustomDims((d) => ({ ...d, [k]: e.target.value }))}
                            className="mt-1 w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 font-mono text-[11px] text-zinc-500">≈ {area} m² — preview scales live to your exact slab</p>
                  </div>
                )}
                <p className="text-xs text-zinc-500">Every court is measured on site before we quote — sizes here set the design proportions.</p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h3 className="font-heading font-black text-2xl">ZONE COLOURS.</h3>
                <div className="flex flex-wrap gap-2">
                  {ZONES.map(([id, label]) => (
                    <Chip key={id} active={activeZone === id} onClick={() => setActiveZone(id)} testid={`zone-${id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full border border-white/20" style={{ backgroundColor: zones[id] }} />{label}
                      </span>
                    </Chip>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {COURT_COLOURS.map((c) => (
                    <button key={c.hex} title={c.name} data-testid={`colour-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setZones((z) => ({ ...z, [activeZone]: c.hex }))}
                      className={`h-9 w-9 rounded-sm ring-2 transition-all ${zones[activeZone] === c.hex ? "ring-neon scale-110" : "ring-white/10 hover:ring-white/40"}`}
                      style={{ backgroundColor: c.hex }} />
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-heading font-black text-2xl">GAME LINES.</h3>
                <p className="text-sm text-zinc-400">Pick up to 3 sports. First = primary lines in your chosen line colour; overlays get contrast colours.</p>
                <div className="flex flex-wrap gap-2">
                  {SPORTS.map((s) => <Chip key={s} active={sports.includes(s)} onClick={() => toggleSport(s)} testid={`sport-${s.toLowerCase()}`}>{sports[0] === s ? `★ ${s}` : s}</Chip>)}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <h3 className="font-heading font-black text-2xl">CENTRE LOGO.</h3>
                <p className="text-sm text-zinc-400">Put a name or brand at centre court — or clear it for a clean look. Have artwork? Mention it at the final step.</p>
                <input value={logoText} maxLength={20} placeholder="e.g. SMITH COURT" data-testid="logo-text-input"
                  onChange={(e) => setLogoText(e.target.value)}
                  className="w-full bg-obsidian-700 border border-white/10 rounded-sm p-3.5 text-sm outline-none focus:border-neon uppercase" />
                <div className="flex flex-wrap gap-2">
                  {COURT_COLOURS.map((c) => (
                    <button key={c.hex} title={c.name} data-testid={`logo-colour-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setLogoColor(c.hex)}
                      className={`h-9 w-9 rounded-sm ring-2 transition-all ${logoColor === c.hex ? "ring-neon scale-110" : "ring-white/10 hover:ring-white/40"}`}
                      style={{ backgroundColor: c.hex }} />
                  ))}
                </div>
                {logoText && (
                  <button onClick={() => setLogoText("")} className="text-xs text-zinc-500 hover:text-white" data-testid="logo-clear">
                    Remove logo
                  </button>
                )}
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <h3 className="font-heading font-black text-2xl">EXTRAS.</h3>
                <div className="flex flex-wrap gap-2">
                  {EXTRAS.map((e) => <Chip key={e} active={extras.includes(e)} onClick={() => toggleExtra(e)} testid={`extra-${e.replace(/\s/g, "-").toLowerCase()}`}>{extras.includes(e) && <Check size={13} className="inline mr-1" />}{e}</Chip>)}
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="space-y-4">
                <h3 className="font-heading font-black text-2xl">GET IT QUOTED.</h3>
                <div className="rounded-sm border border-gold/25 bg-obsidian-800 p-4 font-mono text-[11px] leading-relaxed text-zinc-400">
                  {surfaceType} · {finish} · {sizeLabel} · ≈{area} m²<br />
                  Surface {colourName(zones.base)} · Key {colourName(zones.key)} · Border {colourName(zones.border)}<br />
                  Lines: {sports.join(" + ")}{logoText && <> · Logo "{logoText.toUpperCase()}"</>}{extras.length > 0 && <><br />Extras: {extras.join(", ")}</>}
                </div>
                {[["name", "Your name *"], ["email", "Email"], ["phone", "Phone"], ["postcode", "Postcode"]].map(([k, ph]) => (
                  <input key={k} value={contact[k]} placeholder={ph} data-testid={`designer-${k}`}
                    onChange={(e) => setContact((c) => ({ ...c, [k]: e.target.value }))}
                    className="w-full bg-obsidian-700 border border-white/10 rounded-sm p-3.5 text-sm outline-none focus:border-neon" />
                ))}
                <button onClick={submit} disabled={sending} data-testid="designer-submit"
                  className="w-full rounded-sm bg-neon px-6 py-4 font-bold text-obsidian hover:bg-neon-hover transition-colors shadow-neon disabled:opacity-60">
                  {sending ? "Sending…" : "Send My Court Design for a Free Quote"}
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex justify-between">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="designer-back"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white disabled:opacity-30"><ArrowLeft size={16} /> Back</button>
          {step < 6 && (
            <button onClick={() => setStep((s) => s + 1)} data-testid="designer-next"
              className="inline-flex items-center gap-2 rounded-sm border border-neon/50 px-5 py-2.5 text-sm font-bold text-neon hover:bg-neon hover:text-obsidian transition-colors">
              Next <ArrowRight size={16} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
